// Telephony Console — Asterisk config preview + simulator controls (Slice 4).
import { useEffect, useState } from "react";
import { Loader2, Save, Phone, Radio, Copy, PlayCircle, ShieldCheck, AlertTriangle, AlertCircle, Info, Wrench, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

function ConfBlock({ title, code, testId }) {
  function copy() {
    navigator.clipboard?.writeText(code || "");
    toast.success("Copied to clipboard");
  }
  return (
    <Card className="p-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-muted/40 border-b">
        <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{title}</span>
        <Button data-testid={`${testId}-copy`} onClick={copy} variant="ghost" size="sm" className="h-7 gap-1"><Copy className="w-3.5 h-3.5"/>Copy</Button>
      </div>
      <pre data-testid={testId} className="p-4 text-xs font-mono overflow-x-auto bg-card max-h-96">{code || "; (empty — add resources to generate)"}</pre>
    </Card>
  );
}

const KIND_LABEL = {
  sip_trunks: "Trunks", dids: "DIDs", queues: "Queues", ivr_flows: "IVR flows",
  voicemail_boxes: "Voicemail", users: "Users", music_on_hold: "Music on Hold",
  audio_prompts: "Audio prompts", campaigns: "Campaigns",
};

const SEVERITY_META = {
  error:   { icon: AlertCircle,    cls: "text-red-600",  bg: "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900",       chip: "destructive" },
  warning: { icon: AlertTriangle,  cls: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900", chip: "secondary"   },
  info:    { icon: Info,           cls: "text-sky-600",  bg: "bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900",       chip: "outline"     },
};

function ValidationPanel({ v, validating, fixing, onRevalidate, onAutoFix }) {
  if (!v) return <Card className="p-6 text-center text-muted-foreground">Loading validation…</Card>;
  const s = v.summary || { total: 0, errors: 0, warnings: 0, infos: 0, blocking: false };
  const issues = v.issues || [];
  const grouped = issues.reduce((acc, i) => { (acc[i.kind] = acc[i.kind] || []).push(i); return acc; }, {});
  const allClear = s.errors === 0 && s.warnings === 0 && s.infos === 0;

  return (
    <div className="space-y-4" data-testid="tel-validation-panel">
      <Card className={`p-4 border ${s.blocking ? "border-red-300 bg-red-50/40 dark:bg-red-950/20" : allClear ? "border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/20" : "border-amber-300 bg-amber-50/40 dark:bg-amber-950/20"}`}>
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            {allClear ? (
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            ) : s.blocking ? (
              <AlertCircle className="w-6 h-6 text-red-600" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            )}
            <div>
              <div className="font-display font-bold text-base" data-testid="tel-validation-headline">
                {allClear ? "All checks passed" : s.blocking ? `${s.errors} blocking error${s.errors!==1?"s":""}` : `${s.warnings} warning${s.warnings!==1?"s":""} — non-blocking`}
              </div>
              <div className="text-xs text-muted-foreground">
                {s.total} total — {s.errors} errors · {s.warnings} warnings · {s.infos} info
                {v.auto_fixable > 0 && <> · <span className="font-medium">{v.auto_fixable} auto-fixable</span></>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="tel-validate-rerun" onClick={onRevalidate} disabled={validating} variant="outline" size="sm" className="gap-1.5">
              {validating ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <ShieldCheck className="w-3.5 h-3.5"/>}
              Re-validate
            </Button>
            <Button data-testid="tel-autofix-all" onClick={() => onAutoFix(null)} disabled={fixing || v.auto_fixable === 0} size="sm" className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
              {fixing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Wrench className="w-3.5 h-3.5"/>}
              Auto-fix {v.auto_fixable ? `(${v.auto_fixable})` : ""}
            </Button>
          </div>
        </div>
      </Card>

      {allClear && (
        <Card className="p-8 text-center" data-testid="tel-validation-clean">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
          <div className="font-display font-bold">Configs are ready to push</div>
          <div className="text-sm text-muted-foreground mt-1">No orphan routes, dangling references or missing required fields detected.</div>
        </Card>
      )}

      {Object.entries(grouped).map(([kind, items]) => (
        <Card key={kind} className="p-0 overflow-hidden" data-testid={`tel-validation-group-${kind}`}>
          <div className="px-4 py-2 bg-muted/40 border-b flex items-center justify-between">
            <span className="font-display text-sm font-bold">{KIND_LABEL[kind] || kind} <span className="text-muted-foreground font-normal">— {items.length}</span></span>
          </div>
          <div className="divide-y">
            {items.map((it, idx) => {
              const meta = SEVERITY_META[it.severity] || SEVERITY_META.info;
              const Icon = meta.icon;
              return (
                <div key={`${it.code}-${it.resource_id}-${idx}`} className={`flex items-start gap-3 px-4 py-3 ${meta.bg}`} data-testid={`tel-issue-${it.code}-${idx}`}>
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.cls}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={meta.chip} className="text-[10px] uppercase tracking-wide">{it.severity}</Badge>
                      <code className="text-[10px] font-mono text-muted-foreground">{it.code}</code>
                      {it.resource_name && <span className="text-xs font-medium truncate">{it.resource_name}</span>}
                    </div>
                    <div className="text-sm mt-1">{it.message}</div>
                  </div>
                  {it.fix && (
                    <Button data-testid={`tel-issue-fix-${idx}`} onClick={() => onAutoFix([it.code])} disabled={fixing} size="sm" variant="outline" className="gap-1 h-7">
                      <Wrench className="w-3 h-3"/>Fix
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function TelephonyPage() {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sim, setSim] = useState({ extension: "1002", destination: "+91 90000 00000" });
  const [events, setEvents] = useState([]);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [fixing, setFixing] = useState(false);
  const { canWrite } = useCurrentModulePerms();

  async function loadAll() {
    setLoading(true);
    try {
      const [c, p, e, v] = await Promise.all([
        api.get("/admin/telephony/config"),
        api.get("/admin/telephony/preview"),
        api.get("/admin/telephony/events", { params: { limit: 25 } }),
        api.get("/admin/telephony/validate"),
      ]);
      setCfg(c.data); setPreview(p.data); setEvents(e.data || []); setValidation(v.data);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadAll(); }, []);

  async function revalidate() {
    setValidating(true);
    try {
      const r = await api.get("/admin/telephony/validate");
      setValidation(r.data);
      const s = r.data?.summary;
      if (s?.errors === 0 && s?.warnings === 0) toast.success("All checks passed");
      else toast.message(`${s?.errors||0} error(s), ${s?.warnings||0} warning(s)`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setValidating(false); }
  }

  async function autoFix(codes) {
    setFixing(true);
    try {
      const r = await api.post("/admin/telephony/auto-fix", codes ? { codes } : {});
      setValidation({ summary: r.data.summary, issues: r.data.issues, auto_fixable: r.data.auto_fixable });
      toast.success(`Applied ${r.data.applied.length} fix(es)`);
      // refresh stats
      const p = await api.get("/admin/telephony/preview");
      setPreview(p.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setFixing(false); }
  }

  async function saveCfg() {
    setSaving(true);
    try {
      const body = { mode: cfg.mode, asterisk_host: cfg.asterisk_host||"", ari_username: cfg.ari_username||"", ami_username: cfg.ami_username||"", external_ip: cfg.external_ip||"" };
      await api.put("/admin/telephony/config", body);
      toast.success("Telephony config saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  }

  async function regenerate() {
    try {
      const r = await api.post("/admin/telephony/regenerate");
      toast.success(r.data?.detail || "Regenerated");
      loadAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.summary) {
        toast.error(`Blocked: ${d.summary.errors} error(s). Open the Validation tab.`);
        // refresh validation panel so the user sees the issues
        try { const v = await api.get("/admin/telephony/validate"); setValidation(v.data); } catch {}
      } else {
        toast.error(formatErr(e));
      }
    }
  }

  async function simulate() {
    try {
      await api.post("/admin/telephony/simulate-call", { extension: sim.extension, destination: sim.destination });
      toast.success(`Originated → ${sim.destination}`);
      const e = await api.get("/admin/telephony/events", { params: { limit: 25 } });
      setEvents(e.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  }

  if (loading || !cfg) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="telephony-title">Telephony Engine</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Asterisk integration. <strong>Simulator mode</strong> generates previewable configs without touching a real PBX.
            <strong> Live mode</strong> writes to /etc/asterisk and reloads — requires the bundled Asterisk container.
          </p>
        </div>
        <Badge variant="outline" className="gap-1.5"><Radio className="w-3.5 h-3.5"/>{cfg.mode}</Badge>
      </div>

      <Card className="p-5 mb-5">
        <h2 className="font-display font-bold mb-3 flex items-center gap-2"><Phone className="w-4 h-4 text-primary"/> Engine mode</h2>
        <div className="grid sm:grid-cols-4 gap-3">
          <div><Label>Mode</Label>
            <Select value={cfg.mode} onValueChange={(v)=>setCfg({...cfg, mode:v})}>
              <SelectTrigger data-testid="tel-mode" className="h-9"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="simulator">Simulator (preview only)</SelectItem>
                <SelectItem value="live">Live (Asterisk PBX)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-3 grid sm:grid-cols-3 gap-3">
            <div><Label>Asterisk host</Label><Input data-testid="tel-host" disabled={cfg.mode!=="live"} value={cfg.asterisk_host||""} onChange={(e)=>setCfg({...cfg, asterisk_host:e.target.value})} placeholder="asterisk.connect7.local" /></div>
            <div><Label>ARI username</Label><Input data-testid="tel-ari-user" disabled={cfg.mode!=="live"} value={cfg.ari_username||""} onChange={(e)=>setCfg({...cfg, ari_username:e.target.value})} /></div>
            <div><Label>AMI username</Label><Input data-testid="tel-ami-user" disabled={cfg.mode!=="live"} value={cfg.ami_username||""} onChange={(e)=>setCfg({...cfg, ami_username:e.target.value})} /></div>
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50/50 dark:bg-sky-950/20 p-4">
          <Label className="flex items-center gap-1.5 font-display font-bold text-sm">
            <Info className="w-3.5 h-3.5 text-sky-600" /> External / NAT Public IP
          </Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2 max-w-3xl">
            The server's <strong>public/NAT IP</strong>. Asterisk advertises this in SDP to peers <em>outside</em> your LAN
            (e.g. a cloud SIP trunk like Plivo) so return audio is routable. LAN WebRTC agents are unaffected (they match <code>local_net</code>).
            Leave blank for a pure-LAN install. <strong>After changing this, click Regenerate, then restart Asterisk</strong> (<code>docker compose restart asterisk</code>) — SIP transports are not hot-reloadable.
          </p>
          <Input
            data-testid="tel-external-ip"
            disabled={cfg.mode!=="live"}
            value={cfg.external_ip||""}
            onChange={(e)=>setCfg({...cfg, external_ip:e.target.value})}
            placeholder="e.g. 203.0.113.45  (your office static public IP)"
            className="max-w-md font-mono"
          />
        </div>
        <div className="flex items-center gap-2 mt-4">
          {canWrite && <Button data-testid="tel-save" onClick={saveCfg} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Save className="w-4 h-4"/>Save</Button>}
          {canWrite && <Button data-testid="tel-regen" onClick={regenerate} variant="outline" className="gap-1.5"><Radio className="w-4 h-4"/>Regenerate</Button>}
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        <Card className="p-3"><div className="text-overline text-muted-foreground">Trunks</div><div className="text-2xl font-display font-bold">{preview?.stats?.trunks ?? "—"}</div></Card>
        <Card className="p-3"><div className="text-overline text-muted-foreground">DIDs</div><div className="text-2xl font-display font-bold">{preview?.stats?.dids ?? "—"}</div></Card>
        <Card className="p-3"><div className="text-overline text-muted-foreground">Queues</div><div className="text-2xl font-display font-bold">{preview?.stats?.queues ?? "—"}</div></Card>
        <Card className="p-3"><div className="text-overline text-muted-foreground">IVRs</div><div className="text-2xl font-display font-bold">{preview?.stats?.ivrs ?? "—"}</div></Card>
        <Card className="p-3"><div className="text-overline text-muted-foreground">Agents w/ ext</div><div className="text-2xl font-display font-bold">{preview?.stats?.agents_with_ext ?? "—"}</div></Card>
      </div>

      <Tabs defaultValue="validation">
        <TabsList>
          <TabsTrigger value="validation" data-testid="tel-tab-validation" className="gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5"/>Validation
            {validation?.summary?.errors > 0 && (
              <Badge variant="destructive" className="ml-1 h-4 px-1.5 text-[10px]" data-testid="tel-validation-error-pill">{validation.summary.errors}</Badge>
            )}
            {validation?.summary?.errors === 0 && validation?.summary?.warnings > 0 && (
              <Badge variant="outline" className="ml-1 h-4 px-1.5 text-[10px] border-amber-400 text-amber-600">{validation.summary.warnings}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pjsip_transports" data-testid="tel-tab-transports">pjsip.conf (transports)</TabsTrigger>
          <TabsTrigger value="pjsip_trunks" data-testid="tel-tab-trunks">pjsip.conf (trunks)</TabsTrigger>
          <TabsTrigger value="pjsip_agents" data-testid="tel-tab-agents">pjsip.conf (agents)</TabsTrigger>
          <TabsTrigger value="dialplan" data-testid="tel-tab-dialplan">extensions.conf</TabsTrigger>
          <TabsTrigger value="queues_conf" data-testid="tel-tab-queues">queues.conf</TabsTrigger>
          <TabsTrigger value="simulator" data-testid="tel-tab-sim">Simulator</TabsTrigger>
        </TabsList>
        <TabsContent value="validation" className="mt-3">
          <ValidationPanel v={validation} validating={validating} fixing={fixing} onRevalidate={revalidate} onAutoFix={autoFix} />
        </TabsContent>
        <TabsContent value="pjsip_transports" className="mt-3"><ConfBlock title="pjsip.conf — SIP/WSS transports (NAT)" code={preview?.pjsip_transports} testId="conf-pjsip-transports" /></TabsContent>
        <TabsContent value="pjsip_trunks" className="mt-3"><ConfBlock title="pjsip.conf — SIP trunks" code={preview?.pjsip_trunks} testId="conf-pjsip-trunks" /></TabsContent>
        <TabsContent value="pjsip_agents" className="mt-3"><ConfBlock title="pjsip.conf — WebRTC agents" code={preview?.pjsip_agents} testId="conf-pjsip-agents" /></TabsContent>
        <TabsContent value="dialplan" className="mt-3"><ConfBlock title="extensions.conf — Dialplan" code={preview?.dialplan} testId="conf-dialplan" /></TabsContent>
        <TabsContent value="queues_conf" className="mt-3"><ConfBlock title="queues.conf — ACD queues" code={preview?.queues_conf} testId="conf-queues" /></TabsContent>

        <TabsContent value="simulator" className="mt-3">
          <Card className="p-5">
            <h3 className="font-display font-bold mb-3">Originate a call (simulator)</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>From extension</Label><Input data-testid="sim-ext" value={sim.extension} onChange={(e)=>setSim({...sim, extension:e.target.value})} /></div>
              <div><Label>To destination</Label><Input data-testid="sim-dest" value={sim.destination} onChange={(e)=>setSim({...sim, destination:e.target.value})} /></div>
              <div className="flex items-end"><Button data-testid="sim-go" onClick={simulate} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 w-full"><PlayCircle className="w-4 h-4"/>Originate</Button></div>
            </div>
            <div className="border-t mt-5 pt-3">
              <h4 className="font-display font-bold mb-2">Recent events</h4>
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left"><th className="px-3 py-2">When</th><th className="px-3 py-2">Kind</th><th className="px-3 py-2">Detail</th></tr></thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-t" data-testid={`tel-ev-${e.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{new Date(e.ts).toLocaleTimeString()}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{e.kind}</Badge></td>
                      <td className="px-3 py-2 font-mono"><code>{JSON.stringify(e.payload).slice(0,120)}</code></td>
                    </tr>
                  ))}
                  {events.length === 0 && <tr><td colSpan={3} className="text-center py-6 text-muted-foreground">No events yet — click Originate to fire one.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
